import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, MoreHorizontal, Trash2, Edit2, Star, X, Check,
  DollarSign, List, ExternalLink, Link, GripVertical, LayoutGrid, LayoutList
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CARD_CLASS =
  'bg-black border border-gray-400 rounded-xl transition-all duration-300 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)]';

const CATEGORIES = [
  'Education', 'Lifestyle', 'Equipment', 'Traveling', 'Emergency',
  'Real Estate', 'Entertainment', 'Health', 'Vehicle', 'Business', 'General'
];

const EMOJI_PRESETS = [
  '🎓','🏠','💻','✈️','🚨','🚗','📷','🌸','💍','🏖️','🎸','📚',
  '🏋️','🐾','🎮','🛒','💰','🏦','🌍','🎁','⚽','🎨','🍕','🔒'
];

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

const getPercent = (saved, goal) => {
  if (!goal || goal <= 0) return 0;
  return Math.min(100, Math.round((saved / goal) * 100));
};

// ── Toggle component ──────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testId || 'toggle'}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 transition-colors duration-200 focus:outline-none ${
      checked ? 'bg-purple-600 border-purple-600' : 'bg-gray-700 border-gray-600'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

// ── Goal Card ─────────────────────────────────────────────────────────────────
const GoalCard = ({ goal, onEdit, onDelete, onTogglePriority, dragHandleProps }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const pct = getPercent(goal.saved_amount, goal.goal_amount);
  const remaining = Math.max(0, goal.goal_amount - goal.saved_amount);

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div
      className={`${CARD_CLASS} p-5 flex flex-col gap-3 cursor-pointer`}
      data-testid={`goal-card-${goal.goal_id}`}
      onClick={() => onEdit(goal)}
    >
      {/* Header row */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          {/* Drag handle */}
          <div
            {...dragHandleProps}
            className="text-gray-700 hover:text-gray-400 cursor-grab active:cursor-grabbing transition-colors flex-shrink-0 mt-0.5"
            onClick={e => e.stopPropagation()}
            title="Drag to reorder"
          >
            <GripVertical size={16} />
          </div>
          <div className="w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center text-2xl flex-shrink-0">
            {goal.emoji || '💰'}
          </div>
          <div>
            <h3 className="text-white font-semibold text-base leading-tight">{goal.name}</h3>
            <p className="text-gray-400 text-sm mt-0.5">Total: {formatCurrency(goal.goal_amount)}</p>
          </div>
        </div>
        <div className="relative" ref={menuRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(p => !p); }}
            className="text-gray-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-800"
            data-testid={`goal-menu-${goal.goal_id}`}
          >
            <MoreHorizontal size={16} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-8 w-44 bg-black border border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
              <button
                onClick={(e) => { e.stopPropagation(); onTogglePriority(goal); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <Star size={14} className={goal.is_priority ? 'text-yellow-400' : ''} />
                {goal.is_priority ? 'Remove Priority' : 'Mark Priority'}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(goal); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <Edit2 size={14} /> Edit Goal
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(goal.goal_id); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-600 rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">{formatCurrency(goal.saved_amount)} spent</span>
          <span className="text-gray-300 font-semibold">{pct}%</span>
        </div>
      </div>

      {/* Stats rows */}
      <div className="space-y-1.5 border-t border-gray-800 pt-3">
        {goal.due_date && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Due Date</span>
            <span className="text-white font-medium">{goal.due_date}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Category</span>
          <span className="text-white font-medium">{goal.category}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Remaining</span>
          <span className="text-white font-medium">{formatCurrency(remaining)}</span>
        </div>
      </div>

      {/* Budget Items breakdown */}
      {goal.items && goal.items.length > 0 && (
        <div className="space-y-1.5 border-t border-gray-800 pt-3">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <List size={11} /> Budget Breakdown
          </p>
          {/* Scroll after 7 items — each row ~28px, 7 rows = 196px */}
          <div className="overflow-y-auto" style={{ maxHeight: '196px' }}>
            <div className="space-y-1.5 pr-1">
              {goal.items.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1 pr-2">
                    <span className="text-gray-400 truncate">{item.name}</span>
                    {(Number(item.quantity) || 1) > 1 && (
                      <span className="text-gray-600 text-xs flex-shrink-0">×{Number(item.quantity)}</span>
                    )}
                    {item.url && (
                      <a
                        href={item.url.startsWith('http') ? item.url : `https://${item.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-purple-500 hover:text-purple-300 transition-colors flex-shrink-0"
                        title={item.url}
                      >
                        <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                  <span className="text-white font-medium tabular-nums">
                    {formatCurrency(Number(item.price) || 0)}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between text-sm border-t border-gray-800 pt-1.5 mt-1">
            <span className="text-gray-300 font-semibold">Total</span>
            <span className="text-purple-400 font-bold tabular-nums">
              {formatCurrency(goal.items.reduce((s, i) => s + (Number(i.price) || 0), 0))}
            </span>
          </div>
        </div>
      )}

    </div>
  );
};

// ── Goal List Row (List View) ─────────────────────────────────────────────────
const GoalListRow = ({ goal, onEdit, onDelete, onTogglePriority }) => {
  const [expanded, setExpanded] = useState(true);
  const pct = getPercent(goal.saved_amount, goal.goal_amount);
  const remaining = Math.max(0, goal.goal_amount - goal.saved_amount);
  const hasItems = goal.items && goal.items.length > 0;

  return (
    <div className={`${CARD_CLASS} overflow-hidden`} data-testid={`goal-list-row-${goal.goal_id}`}>
      {/* Goal header row */}
      <div
        className="flex items-center gap-4 px-5 py-4 cursor-pointer select-none"
        onClick={() => onEdit(goal)}
      >
        {/* Emoji */}
        <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center text-xl flex-shrink-0">
          {goal.emoji || '💰'}
        </div>

        {/* Name + category */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold text-sm truncate">{goal.name}</span>
            {goal.is_priority && <Star size={12} className="text-yellow-400 flex-shrink-0" />}
          </div>
          <span className="text-xs text-gray-500">{goal.category}</span>
        </div>

        {/* Progress bar + % */}
        <div className="hidden sm:flex items-center gap-3 w-40 flex-shrink-0">
          <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full bg-purple-600 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs text-gray-400 tabular-nums w-8 text-right">{pct}%</span>
        </div>

        {/* Stats */}
        <div className="hidden md:flex items-center gap-6 flex-shrink-0">
          <div className="text-right">
            <p className="text-xs text-gray-500">Goal</p>
            <p className="text-sm font-semibold text-white tabular-nums">{formatCurrency(goal.goal_amount)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Spent</p>
            <p className="text-sm font-semibold text-purple-400 tabular-nums">{formatCurrency(goal.saved_amount)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Remaining</p>
            <p className="text-sm font-semibold text-white tabular-nums">{formatCurrency(remaining)}</p>
          </div>
          {goal.due_date && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Due</p>
              <p className="text-sm text-white">{goal.due_date}</p>
            </div>
          )}
        </div>

        {/* Expand toggle + menu */}
        <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
          {hasItems && (
            <button
              onClick={() => setExpanded(p => !p)}
              className="text-gray-500 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-gray-800"
              title={expanded ? 'Collapse items' : 'Expand items'}
            >
              <svg className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
          <button
            onClick={() => { onEdit(goal); }}
            className="text-gray-500 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-gray-800"
          >
            <Edit2 size={14} />
          </button>
          <button
            onClick={() => onDelete(goal.goal_id)}
            className="text-gray-500 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-500/10"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Budget items table */}
      {hasItems && expanded && (
        <div className="border-t border-gray-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left px-5 py-2 text-xs text-gray-500 font-medium">Item</th>
                <th className="text-center px-3 py-2 text-xs text-gray-500 font-medium">Qty</th>
                <th className="text-right px-3 py-2 text-xs text-gray-500 font-medium">Unit Price</th>
                <th className="text-right px-5 py-2 text-xs text-gray-500 font-medium">Total</th>
                <th className="text-right px-5 py-2 text-xs text-gray-500 font-medium w-10">Link</th>
              </tr>
            </thead>
            <tbody>
              {goal.items.map((item, idx) => {
                const qty = Number(item.quantity) || 1;
                const lineTotal = Number(item.price) || 0;
                return (
                  <tr key={idx} className="border-b border-gray-900 last:border-0 hover:bg-gray-900/40 transition-colors">
                    <td className="px-5 py-2.5 text-gray-300">{item.name}</td>
                    <td className="px-3 py-2.5 text-gray-400 text-center tabular-nums">{qty}</td>
                    <td className="px-3 py-2.5 text-gray-400 tabular-nums text-right">{formatCurrency(item.price)}</td>
                    <td className="px-5 py-2.5 text-white font-medium tabular-nums text-right">{formatCurrency(lineTotal)}</td>
                    <td className="px-5 py-2.5 text-right">
                      {item.url ? (
                        <a
                          href={item.url.startsWith('http') ? item.url : `https://${item.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-500 hover:text-purple-300 transition-colors inline-flex justify-end"
                          title={item.url}
                          onClick={e => e.stopPropagation()}
                        >
                          <ExternalLink size={13} />
                        </a>
                      ) : (
                        <span className="text-gray-700">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-900/30">
                <td className="px-5 py-2.5 text-gray-400 font-semibold text-xs uppercase tracking-wide">Total</td>
                <td colSpan={2} />
                <td className="px-5 py-2.5 text-purple-400 font-bold tabular-nums text-right">
                  {formatCurrency(goal.items.reduce((s, i) => s + (Number(i.price) || 0), 0))}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {!hasItems && (
        <div className="border-t border-gray-800 px-5 py-3 text-xs text-gray-600 italic">
          No budget items — click to add items
        </div>
      )}
    </div>
  );
};

// ── Create / Edit Modal ───────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: '', emoji: '💰', goal_amount: '', saved_amount: '',
  due_date: '', category: 'General', is_priority: false,
  items: []
};

const GoalModal = ({ goal, onClose, onSave, isSaving }) => {
  const [form, setForm] = useState(goal ? { ...goal } : { ...EMPTY_FORM });
  const [items, setItems] = useState(goal?.items || []);
  const [newItemName, setNewItemName] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [newItemQty, setNewItemQty] = useState('1');
  const [newItemUrl, setNewItemUrl] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const isEdit = !!goal;

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const itemsTotal = items.reduce((s, i) => s + (Number(i.price) || 0), 0);
  const hasItems = items.length > 0;

  const addItem = () => {
    if (!newItemName.trim()) return toast.error('Item name is required');
    setItems(prev => [...prev, {
      name: newItemName.trim(),
      price: Number(newItemPrice) || 0,
      quantity: Number(newItemQty) || 1,
      url: newItemUrl.trim()
    }]);
    setNewItemName('');
    setNewItemPrice('');
    setNewItemQty('1');
    setNewItemUrl('');
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const updateItem = (idx, field, value) =>
    setItems(prev => prev.map((it, i) => i === idx
      ? { ...it, [field]: (field === 'price' || field === 'quantity') ? Number(value) || 0 : value }
      : it
    ));

  const reorderItems = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(items);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setItems(reordered);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Goal name is required');
    const effectiveAmount = hasItems ? itemsTotal : Number(form.goal_amount);
    if (!effectiveAmount || effectiveAmount <= 0) return toast.error('Add budget items or enter a goal amount');
    onSave({
      ...form,
      goal_amount: effectiveAmount,
      saved_amount: Number(form.saved_amount) || 0,
      items,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}>
      <div className={`${CARD_CLASS} w-full max-w-xl max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800 flex-shrink-0">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Edit Goal' : 'Create New Goal'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Emoji + Name */}
          <div className="flex gap-3">
            <div className="relative">
              <button type="button" onClick={() => setShowEmojiPicker(p => !p)}
                className="w-12 h-12 bg-gray-900 border border-gray-700 rounded-xl text-2xl hover:border-gray-500 transition-colors flex items-center justify-center">
                {form.emoji}
              </button>
              {showEmojiPicker && (
                <div className="absolute top-14 left-0 bg-black border border-gray-700 rounded-xl p-3 z-50 grid grid-cols-6 gap-2 w-56 shadow-xl">
                  {EMOJI_PRESETS.map(e => (
                    <button key={e} type="button"
                      onClick={() => { set('emoji', e); setShowEmojiPicker(false); }}
                      className="text-xl hover:bg-gray-800 rounded-lg p-1 transition-colors">{e}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-400 mb-1">Goal Name *</label>
              <input
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g. Japan Vacation"
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors"
                data-testid="goal-name-input"
              />
            </div>
          </div>

          {/* Goal Amount + Saved */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Goal Amount ($)
                {hasItems && <span className="ml-1 text-purple-400 text-xs">(auto from items)</span>}
              </label>
              <input
                type="number" min="1" step="any"
                value={hasItems ? itemsTotal : form.goal_amount}
                onChange={e => !hasItems && set('goal_amount', e.target.value)}
                readOnly={hasItems}
                placeholder="10,000"
                className={`w-full px-3 py-2.5 border rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors ${
                  hasItems ? 'bg-gray-950 border-gray-800 text-purple-400 cursor-not-allowed' : 'bg-gray-900 border-gray-700'
                }`}
                data-testid="goal-amount-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Total Spent ($)</label>
              <input
                type="number" min="0" step="any"
                value={form.saved_amount}
                onChange={e => set('saved_amount', e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors"
                data-testid="saved-amount-input"
              />
            </div>
          </div>

          {/* Category + Due Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Category</label>
              <select
                value={form.category}
                onChange={e => set('category', e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-xl text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
                data-testid="goal-category-input"
              >
                {CATEGORIES.map(c => <option key={c} value={c} className="bg-gray-900">{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Due Date</label>
              <input
                value={form.due_date}
                onChange={e => set('due_date', e.target.value)}
                placeholder="e.g. Dec, 2028"
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors"
                data-testid="goal-due-date-input"
              />
            </div>
          </div>

          {/* ── Budget Items ─────────────────────────────────────────────── */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign size={14} className="text-purple-400" />
                <p className="text-white text-sm font-semibold">Budget Items</p>
              </div>
              {hasItems && (
                <span className="text-xs text-purple-400 font-semibold tabular-nums">
                  Total: {formatCurrency(itemsTotal)}
                </span>
              )}
            </div>
            <p className="text-gray-500 text-xs -mt-1">Break down your goal into individual expenses</p>

            {/* Existing items */}
            {items.length > 0 && (
              <DragDropContext onDragEnd={reorderItems}>
                <Droppable droppableId="modal-items">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                      {items.map((item, idx) => (
                        <Draggable key={idx} draggableId={`item-${idx}`} index={idx}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{ ...provided.draggableProps.style, opacity: snapshot.isDragging ? 0.8 : 1 }}
                              className="space-y-1.5"
                              data-testid={`budget-item-${idx}`}
                            >
                              {/* Row 1: grip | name | qty | price each | delete */}
                              <div className="flex items-center gap-2">
                                {/* Drag handle */}
                                <div
                                  {...provided.dragHandleProps}
                                  className="text-gray-700 hover:text-gray-400 cursor-grab active:cursor-grabbing transition-colors flex-shrink-0"
                                  title="Drag to reorder"
                                >
                                  <GripVertical size={14} />
                                </div>
                                <input
                                  value={item.name}
                                  onChange={e => updateItem(idx, 'name', e.target.value)}
                                  placeholder="Item name"
                                  className="flex-1 px-3 py-1.5 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors"
                                />
                                {/* Qty */}
                                <div className="relative w-16 flex-shrink-0">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">×</span>
                                  <input
                                    type="number" min="1" step="1"
                                    value={item.quantity ?? 1}
                                    onChange={e => updateItem(idx, 'quantity', e.target.value)}
                                    className="w-full pl-5 pr-2 py-1.5 bg-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 transition-colors tabular-nums"
                                    title="Quantity"
                                  />
                                </div>
                                {/* Unit price */}
                                <div className="relative w-28 flex-shrink-0">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                  <input
                                    type="number" min="0" step="any"
                                    value={item.price}
                                    onChange={e => updateItem(idx, 'price', e.target.value)}
                                    placeholder="0"
                                    className="w-full pl-6 pr-2 py-1.5 bg-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 transition-colors tabular-nums"
                                    title="Unit price"
                                  />
                                </div>
                                <button type="button" onClick={() => removeItem(idx)}
                                  className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0">
                                  <X size={15} />
                                </button>
                              </div>
                              {/* Row 2: URL */}
                              <div className="flex items-center gap-2 pl-6">
                                <div className="flex items-center flex-1 gap-1.5 px-3 py-1.5 bg-black border border-gray-800 rounded-lg focus-within:border-purple-500 transition-colors">
                                  <Link size={12} className="text-gray-600 flex-shrink-0" />
                                  <input
                                    value={item.url || ''}
                                    onChange={e => updateItem(idx, 'url', e.target.value)}
                                    placeholder="Add link or URL (optional)"
                                    className="flex-1 bg-transparent text-white text-xs placeholder-gray-700 focus:outline-none"
                                    data-testid={`item-url-input-${idx}`}
                                  />
                                </div>
                                <div className="w-[23px] flex-shrink-0" />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {/* Total row */}
                      <div className="flex items-center justify-between border-t border-gray-700 pt-2 mt-1">
                        <span className="text-gray-400 text-sm font-medium">Total</span>
                        <span className="text-purple-400 font-bold text-sm tabular-nums">{formatCurrency(itemsTotal)}</span>
                      </div>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            {/* Add new item row */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <input
                  value={newItemName}
                  onChange={e => setNewItemName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addItem())}
                  placeholder="Add item (e.g. Flight tickets)"
                  className="flex-1 px-3 py-1.5 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors"
                  data-testid="new-item-name-input"
                />
                {/* Qty */}
                <div className="relative w-16 flex-shrink-0">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">×</span>
                  <input
                    type="number" min="1" step="1"
                    value={newItemQty}
                    onChange={e => setNewItemQty(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addItem())}
                    placeholder="1"
                    className="w-full pl-5 pr-2 py-1.5 bg-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
                    data-testid="new-item-qty-input"
                    title="Quantity"
                  />
                </div>
                {/* Unit price */}
                <div className="relative w-28 flex-shrink-0">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <input
                    type="number" min="0" step="any"
                    value={newItemPrice}
                    onChange={e => setNewItemPrice(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addItem())}
                    placeholder="0"
                    className="w-full pl-6 pr-2 py-1.5 bg-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
                    data-testid="new-item-price-input"
                    title="Unit price"
                  />
                </div>
                <button type="button" onClick={addItem}
                  data-testid="add-item-btn"
                  className="flex-shrink-0 p-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors">
                  <Plus size={15} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center flex-1 gap-1.5 px-3 py-1.5 bg-black border border-gray-800 rounded-lg focus-within:border-purple-500 transition-colors">
                  <Link size={12} className="text-gray-600 flex-shrink-0" />
                  <input
                    value={newItemUrl}
                    onChange={e => setNewItemUrl(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addItem())}
                    placeholder="Link or URL (optional)"
                    className="flex-1 bg-transparent text-white text-xs placeholder-gray-700 focus:outline-none"
                    data-testid="new-item-url-input"
                  />
                </div>
                {/* spacer to align with + button above */}
                <div className="w-[31px] flex-shrink-0" />
              </div>
            </div>
          </div>

          {/* Priority toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star size={14} className={form.is_priority ? 'text-yellow-400' : 'text-gray-600'} />
              <span className="text-sm text-gray-400">Mark as Priority Goal</span>
            </div>
            <Toggle checked={form.is_priority} onChange={v => set('is_priority', v)} testId="priority-toggle" />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-gray-900 text-gray-300 rounded-xl text-sm hover:bg-gray-800 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={isSaving}
              data-testid="save-goal-btn"
              className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
              {isSaving ? (
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              ) : <Check size={14} />}
              {isEdit ? 'Save Changes' : 'Create Goal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Budgeting() {
  const userId = useUserId();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  // ── Total Balance (editable, persisted to backend + localStorage fallback) ──
  const [totalBalance, setTotalBalance] = useState(0);
  const [editingBalance, setEditingBalance] = useState(false);
  const [balanceInput, setBalanceInput] = useState('');

  // Load balance: backend first, localStorage as instant fallback
  useEffect(() => {
    if (!userId) return;
    const stored = localStorage.getItem(`budgeting_balance_${userId}`);
    if (stored) setTotalBalance(Number(stored));
    fetch(`${API_URL}/api/budgeting/settings?user_id=${userId}`)
      .then(r => r.json())
      .then(data => {
        if (typeof data.total_balance === 'number') {
          setTotalBalance(data.total_balance);
          localStorage.setItem(`budgeting_balance_${userId}`, String(data.total_balance));
        }
      })
      .catch(() => { /* silently fall back to localStorage value */ });
  }, [userId]);

  const saveBalance = async () => {
    const val = Number(balanceInput) || 0;
    setTotalBalance(val);
    setEditingBalance(false);
    // Save to localStorage immediately (instant, no network wait)
    if (userId) localStorage.setItem(`budgeting_balance_${userId}`, String(val));
    // Persist to backend
    try {
      await fetch(`${API_URL}/api/budgeting/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, total_balance: val })
      });
    } catch { /* non-critical, localStorage already saved */ }
  };

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchGoals = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/budgeting/goals?user_id=${userId}`);
      const data = await res.json();
      setGoals(data.goals || []);
    } catch {
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGoals(); }, [userId]);

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      if (editingGoal) {
        const res = await fetch(
          `${API_URL}/api/budgeting/goals/${editingGoal.goal_id}?user_id=${userId}`,
          { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) }
        );
        const data = await res.json();
        setGoals(prev => prev.map(g => g.goal_id === editingGoal.goal_id ? data.goal : g));
        toast.success('Goal updated');
      } else {
        const res = await fetch(`${API_URL}/api/budgeting/goals`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, user_id: userId })
        });
        const data = await res.json();
        setGoals(prev => [...prev, data.goal]);
        toast.success(`"${data.goal.name}" goal created`);
      }
      setShowModal(false);
      setEditingGoal(null);
    } catch {
      toast.error('Failed to save goal');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await fetch(`${API_URL}/api/budgeting/goals/${goalId}?user_id=${userId}`, { method: 'DELETE' });
      setGoals(prev => prev.filter(g => g.goal_id !== goalId));
      toast.success('Goal deleted');
    } catch {
      toast.error('Failed to delete goal');
    }
  };

  const handleTogglePriority = async (goal) => {
    const updated = { is_priority: !goal.is_priority };
    try {
      const res = await fetch(
        `${API_URL}/api/budgeting/goals/${goal.goal_id}?user_id=${userId}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updated) }
      );
      const data = await res.json();
      setGoals(prev => prev.map(g => g.goal_id === goal.goal_id ? data.goal : g));
      toast.success(goal.is_priority ? 'Removed from Priority Goals' : 'Added to Priority Goals');
    } catch {
      toast.error('Failed to update goal');
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const totalAllocated = goals.reduce((s, g) => s + (g.goal_amount || 0), 0);
  const totalSpentAll  = goals.reduce((s, g) => s + (g.saved_amount || 0), 0);
  const remaining      = totalBalance - totalAllocated;
  const displayedGoals = activeTab === 'priority' ? goals.filter(g => g.is_priority) : goals;

  // ── Drag & Drop ────────────────────────────────────────────────────────────
  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const sourceIdx = result.source.index;
    const destIdx   = result.destination.index;

    // Work on the full goals array — map displayed indices back to full array
    const displayedIds = displayedGoals.map(g => g.goal_id);
    const reorderedDisplayed = Array.from(displayedIds);
    const [moved] = reorderedDisplayed.splice(sourceIdx, 1);
    reorderedDisplayed.splice(destIdx, 0, moved);

    // Rebuild full goals array preserving non-displayed goals' relative positions
    const nonDisplayed = goals.filter(g => !displayedIds.includes(g.goal_id));
    const newGoals = [
      ...reorderedDisplayed.map(id => goals.find(g => g.goal_id === id)),
      ...nonDisplayed,
    ];
    setGoals(newGoals);

    // Persist silently
    try {
      await fetch(`${API_URL}/api/budgeting/goals/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, goal_ids: newGoals.map(g => g.goal_id) }),
      });
    } catch { /* silent — local order already applied */ }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Page header */}
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Budgets &amp; Goals</h1>
          <p className="text-gray-500 text-sm mt-1">Track your savings goals and budget breakdowns</p>
        </div>

        {/* Balance header */}
        <div className={`${CARD_CLASS} px-6 py-5`}>
          <div className="flex items-start justify-between gap-4">
            {/* Left — editable total balance + stats */}
            <div className="flex-1 min-w-0">
              <p className="text-gray-400 text-sm mb-1">Total Balance</p>
              {editingBalance ? (
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-bold text-gray-400">$</span>
                  <input
                    autoFocus
                    type="number" min="0" step="any"
                    value={balanceInput}
                    onChange={e => setBalanceInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveBalance(); if (e.key === 'Escape') setEditingBalance(false); }}
                    className="text-3xl font-bold bg-transparent border-b-2 border-purple-500 text-white w-44 focus:outline-none tabular-nums"
                    data-testid="balance-input"
                  />
                  <button onClick={saveBalance} className="text-purple-400 hover:text-white transition-colors" data-testid="save-balance-btn">
                    <Check size={18} />
                  </button>
                  <button onClick={() => setEditingBalance(false)} className="text-gray-600 hover:text-white transition-colors">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2.5 group">
                  <p className="text-4xl font-bold text-white tabular-nums" data-testid="total-balance">
                    {formatCurrency(totalBalance)}
                  </p>
                  <button
                    onClick={() => { setBalanceInput(String(totalBalance)); setEditingBalance(true); }}
                    className="text-gray-700 hover:text-gray-400 opacity-0 group-hover:opacity-100 transition-all"
                    data-testid="edit-balance-btn"
                  >
                    <Edit2 size={15} />
                  </button>
                </div>
              )}

              {/* Stats row */}
              <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-800">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Allocated</p>
                  <p className="text-base font-semibold text-white tabular-nums">{formatCurrency(totalAllocated)}</p>
                </div>
                <div className="w-px h-8 bg-gray-800" />
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Remaining</p>
                  <p className={`text-base font-semibold tabular-nums ${remaining >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatCurrency(Math.abs(remaining))}{remaining < 0 ? ' over' : ''}
                  </p>
                </div>
                <div className="w-px h-8 bg-gray-800" />
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Total Spent</p>
                  <p className="text-base font-semibold text-purple-400 tabular-nums">{formatCurrency(totalSpentAll)}</p>
                </div>
                <div className="w-px h-8 bg-gray-800" />
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Goals</p>
                  <p className="text-base font-semibold text-white">{goals.length}</p>
                </div>
              </div>
            </div>

            {/* Right — create button */}
            <button
              onClick={() => { setEditingGoal(null); setShowModal(true); }}
              data-testid="create-goal-btn"
              className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-sm transition-colors"
            >
              <Plus size={16} /> Create Goals
            </button>
          </div>
        </div>

        {/* Tabs + view toggle */}
        <div className="flex items-center justify-between border-b border-gray-800">
          <div className="flex items-center gap-6">
            {[
              { id: 'all', label: 'All Goals' },
              { id: 'priority', label: 'Priority Goals' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`pb-3 text-sm font-semibold transition-all border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'text-purple-400 border-purple-500'
                    : 'text-gray-500 border-transparent hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
            <button
              onClick={() => { setEditingGoal(null); setShowModal(true); }}
              className="pb-3 text-sm text-gray-600 border-b-2 border-transparent -mb-px hover:text-gray-400 transition-colors"
            >
              <Plus size={16} />
            </button>
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-1 mb-1 bg-gray-900 border border-gray-800 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              data-testid="view-grid-btn"
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-white'}`}
              title="Grid view"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              data-testid="view-list-btn"
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-white'}`}
              title="List view"
            >
              <LayoutList size={15} />
            </button>
          </div>
        </div>

        {/* Goals — Grid or List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <svg className="animate-spin w-8 h-8 text-purple-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </div>
        ) : displayedGoals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <div className="w-20 h-20 rounded-2xl bg-gray-900 border border-gray-800 flex items-center justify-center text-4xl">
              💰
            </div>
            <div className="text-center">
              <p className="text-white font-semibold text-lg">
                {activeTab === 'priority' ? 'No priority goals yet' : 'No savings goals yet'}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {activeTab === 'priority'
                  ? 'Mark goals as priority from the ··· menu'
                  : 'Create your first goal to start tracking your savings'}
              </p>
            </div>
            {activeTab === 'all' && (
              <button
                onClick={() => setShowModal(true)}
                className="mt-2 flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-sm transition-colors"
              >
                <Plus size={16} /> Create Your First Goal
              </button>
            )}
          </div>

        ) : viewMode === 'list' ? (
          /* ── List View ── */
          <div className="space-y-3">
            {displayedGoals.map(goal => (
              <GoalListRow
                key={goal.goal_id}
                goal={goal}
                onEdit={(g) => { setEditingGoal(g); setShowModal(true); }}
                onDelete={handleDelete}
                onTogglePriority={handleTogglePriority}
              />
            ))}
          </div>

        ) : (
          /* ── Grid View (drag & drop) ── */
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="goals-grid" direction="horizontal">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                >
                  {displayedGoals.map((goal, index) => (
                    <Draggable key={goal.goal_id} draggableId={goal.goal_id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.85 : 1,
                          }}
                        >
                          <GoalCard
                            goal={goal}
                            dragHandleProps={provided.dragHandleProps}
                            onEdit={(g) => { setEditingGoal(g); setShowModal(true); }}
                            onDelete={handleDelete}
                            onTogglePriority={handleTogglePriority}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <GoalModal
          goal={editingGoal}
          onClose={() => { setShowModal(false); setEditingGoal(null); }}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}
